import React, { useState } from 'react';
import styled from 'styled-components';
import { useCloudSecurity } from '../context/CloudSecurityContext';
import CheckItem from './CheckItem';
import { getFrameworkMappingForCheck } from '../data/frameworkMappings';

const Container = styled.div`
  background: #1a1f3a;
  border-radius: 16px;
  padding: 25px;
  margin-bottom: 25px;
  border: 1px solid #2d3561;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 5px 25px rgba(102, 126, 234, 0.2);
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  cursor: pointer;
`;

const Title = styled.h3`
  font-size: 1.5em;
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CategoryIcon = styled.span`
  font-size: 1.2em;
`;

const Score = styled.div`
  font-size: 1.2em;
  font-weight: bold;
  padding: 8px 16px;
  border-radius: 8px;
  background: rgba(102, 126, 234, 0.2);
`;

const CheckList = styled.div`
  display: ${props => props.collapsed ? 'none' : 'block'};
`;

const ExpandIcon = styled.span`
  transition: transform 0.3s ease;
  transform: ${props => props.collapsed ? 'rotate(180deg)' : 'rotate(0)'};
  margin-left: 15px;
`;

const EmptyFilterMessage = styled.div`
  padding: 15px;
  text-align: center;
  color: #8892b0;
  font-style: italic;
  background: rgba(102, 126, 234, 0.05);
  border-radius: 8px;
  margin-top: 10px;
`;

const Category = ({ category, catIndex, selectedFramework }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { calculateCategoryScore, assessmentResults } = useCloudSecurity();

  const { checked: allChecked, total: allTotal } = calculateCategoryScore(catIndex);
  
  // Filter checks based on selected framework
  const filteredChecks = category.checks.filter(check => {
    if (selectedFramework === 'All') return true;
    const mappings = getFrameworkMappingForCheck(check.title);
    return Object.keys(mappings).includes(selectedFramework);
  });

  // Don't render the category if no checks match the selected framework
  if (filteredChecks.length === 0 && selectedFramework !== 'All') {
    return null;
  }

  // When filtering, show score for the visible subset only
  const checked = selectedFramework === 'All'
    ? allChecked
    : filteredChecks.filter(check => {
        const origIdx = category.checks.findIndex(c => c.title === check.title);
        return assessmentResults[catIndex]?.[origIdx] === true;
      }).length;
  const total = selectedFramework === 'All' ? allTotal : filteredChecks.length;
  
  return (
    <Container>
      <Header onClick={() => setCollapsed(!collapsed)}>
        <Title>
          <CategoryIcon>{category.icon}</CategoryIcon>
          {category.name}
        </Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <Score>{checked}/{total}</Score>
          <ExpandIcon collapsed={collapsed}>▼</ExpandIcon>
        </div>
      </Header>
      
      <CheckList collapsed={collapsed}>
        {filteredChecks.length > 0 ? (
          filteredChecks.map((check, index) => {
            const originalIndex = category.checks.findIndex(c => c.title === check.title);
            return (
              <CheckItem 
                key={originalIndex}
                check={check}
                catIndex={catIndex}
                checkIndex={originalIndex}
              />
            );
          })
        ) : (
          <EmptyFilterMessage>
            No checks in this category match the selected framework.
          </EmptyFilterMessage>
        )}
      </CheckList>
    </Container>
  );
};

export default Category; 